import { AvatarPerson } from '../models/avatar-person.model';

/** The field-name conventions the initials are read from, tried in this order. */
export interface PersonInitialsSource {
  firstName?: string | null;
  lastName?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  additionalOne?: string | null;
  additionalTwo?: string | null;
}

/**
 * The full name of a person, from whichever field their reference carries it in.
 *
 * @param {AvatarPerson | null | undefined} person - The person, if any
 * @returns {string} Their name, or an empty string where none is known
 */
export function resolvePersonName(person: AvatarPerson | null | undefined): string {
  if (!person) {
    return '';
  }
  const nameParts = [person.vorname, person.nachname].map((part) => part?.trim()).filter(Boolean);

  return person.label?.trim() || person.name?.trim() || nameParts.join(' ');
}

/**
 * The initials of a person: the first letters of the first and the last name, in capitals. The first
 * naming convention that carries a name is the one that counts, and a person with one name only gets one letter.
 *
 * @param {PersonInitialsSource | null | undefined} person - The person, if any
 * @returns {string} Up to two letters, or an empty string where no name is known
 */
export function resolvePersonInitials(person: PersonInitialsSource | null | undefined): string {
  if (!person) {
    return '';
  }
  const pairs: (string | null | undefined)[][] = [
    [person.firstName, person.lastName],
    [person.vorname, person.nachname],
    [person.additionalOne, person.additionalTwo],
  ];
  const names = pairs.find((pair) => pair.some((name) => !!name?.trim())) ?? [];

  return names
    .map((name) => name?.trim().charAt(0) ?? '')
    .join('')
    .toUpperCase();
}
